import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Row,
  Col,
  Button,
  Table,
  Form,
  InputGroup,
  Card
} from 'react-bootstrap';
import Select from 'react-select';
import ActionButton from 'components/common/ActionButton';
import Paging from 'components/common-sfims/pagination/paging';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import columns from 'components/pages/haccp/result/hygieneResult/columns';

// global
import { usePageLoadingState } from 'global/pageLoadingState';
import { useConfirmModalState } from 'global/confirmModalState';
// function
import { getLoginInform } from 'function/login';
import { datas2SfshExcelJSON } from 'function/convert';
import { downloadExcelByJSON } from 'function/file';
// types
import { CCP_YN_SELECT, RETURN_YN_SELECT } from 'types/type';
import { RESPONSE_CODE } from 'types/const';
// util
import format from 'utils/string/format';
import comma from 'utils/format/comma';
// api
import { snttnExamRstList } from 'api/haccp/result/hygieneResultApi';
import { inspTypeList } from 'api/haccp/inspStandardInfo/typeMgmtApi';
import { factory, category } from 'api/haccp/commonApi';
// hooks
import useHistoryState from 'hooks/useHistoryState';

/**
 * 검사결과 > 위생검사결과 목록페이지
 */

const List = () => {
  /* router info */
  const history = useHistory();
  const { state } = useLocation();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();
  const { pageLoading, setPageLoading } = usePageLoadingState();

  /* variable */
  const title = '위생검사 결과(하림)';

  /* state */
  const [adminYn, setAdminYn] = useState(null); // Y: 관리자, N: 이천,군산 담당자
  const [totalCount, setTotalCount] = useState(0);
  const [factoryOptions, setFactoryOptions] = useState(null); // 공장 Select Box
  const [categoryOptions, setCategoryOptions] = useState(null); // 검사분류유형 Select Box
  const [inspOptions, setInspOptions] = useState(null); // 검사종류 Select Box
  const [list, setList] = useState([]); // 조회 data

  // 조회조건
  const [pltCd, setPltCd] = useHistoryState('', 'pltCd'); // 공장
  const [ctgryGrpCd, setCtgryGrpCd] = useHistoryState('', 'ctgryGrpCd'); // 검사분류유형
  const [ccpYn, setCcpYn] = useHistoryState('', 'ccpYn'); // CCP여부
  const [sendBkYn, setSendBkYn] = useHistoryState('', 'sendBkYn'); // 반송여부
  const [regDtSt, setRegDtSt] = useHistoryState(moment().startOf('year').toDate(), 'regDtSt'); // 조회시작일
  const [regDtEn, setRegDtEn] = useHistoryState(moment().toDate(), 'regDtEn'); //  조회종료일
  const [divisionKey, setDivisionKey] = useHistoryState('', 'divisionKey'); // 구분선택
  const [divisionValue, setDivisionValue] = useHistoryState(
    '',
    'divisionValue'
  );
  // MultiSelect 검사종류
  const [inspTypeSeqList, setInspTypeSeqList] = useHistoryState(
    [],
    'inspTypeSeqList'
  );
  const [searchParams, setSearchParams] = useHistoryState(
    {
      page: 0,
      size: 11,
      regDtSt: moment().startOf('year').toDate(),
      regDtEn: moment().toDate(),
      sort: 'inspReqDt,desc',
      ...state
    },
    'searchParams'
  );

  // multi Select Custom Style
  const customStyles = {
    control: () => ({
      // none of react-select's styles are passed to <Control />
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'relative',
      height: 29,
      borderRadius: 4
    })
  };

  /* event */

  // 등록 및 상세페이지 이동
  const onDetail = seq => {
    history.push({
      pathname: format('/haccp/result/hygiene/detail/{seq}', { seq }),
      state: searchParams
    });
  };

  // 날짜선택
  const onDateChange = dates => {
    const [start, end] = dates;
    setRegDtSt(start);
    setRegDtEn(end);
  };

  // PDF파일 OPEN
  const onPdfOpen = async url => {
    if (url === null) {
      setConfirmModal({
        isOpen: true,
        title: '확인',
        message: 'PDF파일이 존재하지 않습니다.',
        isConfirmBtn: true
      });
      return;
    }

    const { REACT_APP_API_URL_SFSH } = process.env || {};
    const pdfUrl = `${REACT_APP_API_URL_SFSH}/sfsh/fileView/pdf?filePath=${url}`;
    window.open(pdfUrl, '_blank');
  };

  /* 엑셀다운로드 */
  const onExcelDown = async () => {
    const {
      pltCd,
      ctgryGrpCd,
      ccpYn,
      sendBkYn,
      regDtSt = null,
      regDtEn = null,
      divisionKey,
      divisionValue,
      inspTypeSeqList,
      sort
    } = searchParams || {};

    let startDt = regDtSt;
    let endDt = regDtEn;

    if (regDtSt !== null) {
      startDt = moment(regDtSt).format('YYYY-MM-DD');
    }
    if (regDtEn !== null) {
      endDt = moment(regDtEn).format('YYYY-MM-DD');
    }

    const params = {
      pltCd: pltCd,
      ctgryGrpCd: ctgryGrpCd,
      ccpYn: ccpYn,
      sendBkYn: sendBkYn,
      inspReqDtSt: startDt,
      inspReqDtEn: endDt,
      [divisionKey]: divisionValue,
      inspTypeSeqList:
        inspTypeSeqList?.map(e => {
          return e.seq;
        }) ?? null,
      sort: sort
    };

    Object.keys(params).forEach(
      key => (!params[key] || params[key] === 'ALL') && delete params[key]
    );

    const response = await snttnExamRstList(params);

    const { code, list = [], totalCount = 0 } = response || {};

    if (code !== RESPONSE_CODE.Success || totalCount === 0) return;

    let excelNumber = totalCount;

    downloadExcelByJSON(
      datas2SfshExcelJSON(
        list?.map(e => ({
          ...e,
          no: excelNumber--
        })),
        columns
      ),
      `${title}.xlsx`
    );
  };

  // 조회버튼
  const onSearch = () => {
    setSearchParams({
      ...searchParams,
      page: 0,
      pltCd: pltCd,
      ctgryGrpCd: ctgryGrpCd,
      ccpYn: ccpYn,
      sendBkYn: sendBkYn,
      regDtSt: regDtSt,
      regDtEn: regDtEn,
      divisionKey: divisionKey,
      divisionValue: divisionValue,
      inspTypeSeqList: inspTypeSeqList
    });
  };

  const onSearchRequest = async () => {
    setList([]);
    setTotalCount(0);
    setPageLoading(true);

    const {
      pltCd,
      ctgryGrpCd,
      ccpYn,
      sendBkYn,
      regDtSt = null,
      regDtEn = null,
      divisionKey,
      divisionValue,
      inspTypeSeqList,
      page,
      size,
      sort
    } = searchParams || {};

    let startDt = regDtSt;
    let endDt = regDtEn;

    if (regDtSt !== null) {
      startDt = moment(regDtSt).format('YYYY-MM-DD');
    }
    if (regDtEn !== null) {
      endDt = moment(regDtEn).format('YYYY-MM-DD');
    }

    const params = {
      pltCd: pltCd,
      ctgryGrpCd: ctgryGrpCd,
      ccpYn: ccpYn,
      sendBkYn: sendBkYn,
      inspReqDtSt: startDt,
      inspReqDtEn: endDt,
      [divisionKey]: divisionValue,
      inspTypeSeqList:
        inspTypeSeqList?.map(e => {
          return e.seq;
        }) ?? null,
      page: page,
      size: size,
      sort: sort
    };

    Object.keys(params).forEach(
      key => (!params[key] || params[key] === 'ALL') && delete params[key]
    );

    const response = await snttnExamRstList(params);

    const { code, list = [], totalCount = 0 } = response || {};

    if (code !== RESPONSE_CODE.Success || totalCount === 0) {
      setTotalCount(0);
      setList([]);
      setPageLoading(false);
      return;
    }

    let indexNo = totalCount - searchParams.size * searchParams.page;

    setTotalCount(totalCount);

    setList(
      list?.map(e => ({
        no: indexNo--,
        ...e
      })) ?? []
    );

    setPageLoading(false);
  };

  // 공장목록 API
  const factoryList = async () => {
    const response = await factory();

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;

    setFactoryOptions(list);
  };

  // 검사분류유형 API
  const categoryList = async () => {
    const response = await category();

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;
    setCategoryOptions(list);
  };

  // 검사종류 API
  const inpectionTypeList = async () => {
    const params = {
      useYn: 'Y',
      page: 0,
      sort: 'regDt,asc'
    };

    const response = await inspTypeList(params);

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;
    setInspOptions(
      list?.map(e => ({
        ...e,
        value: e.seq,
        label: e.inspTypeNm
      }))
    );
  };

  // 로그인 정보
  const loginInfo = () => {
    const { adminYn, pltCd } = getLoginInform();

    if (adminYn === 'Y') {
      setAdminYn(adminYn);
      return;
    }

    setPltCd(pltCd);
    setSearchParams({
      ...searchParams,
      page: 0,
      pltCd: pltCd
    });
    setAdminYn(adminYn);
  };

  /* watch */
  useEffect(() => {
    // 공장 API 호출
    factoryList();
    // 검사분류유형 API 호출
    categoryList();
    // 검사종류 API 호출
    inpectionTypeList();
    // 로그인정보
    loginInfo();
  }, []);

  useEffect(() => {
    if (adminYn === null) return;
    onSearchRequest();
  }, [searchParams, adminYn]);

  return (
    <>
      <Card>
        <Card.Header>
          <Row className="align-items-center g-2">
            <Col>
              <h5 className="mb-0">{title}</h5>
            </Col>
            <Col xs={'auto'}>
              <Button
                size="sm"
                variant="secondary"
                className="ms-2"
                onClick={onExcelDown}
                disabled={totalCount === 0}
              >
                엑셀 다운로드
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="pt-0">
          <Form.Group className="mb-3">
            <Row className="ms-0 me-0 mt-3">
              {/* 공장 선택 select-box */}
              <Col sm={1} className="ps-1 pe-1">
                <Form.Select
                  size="sm"
                  onChange={({ target: { value } }) => setPltCd(value)}
                  value={pltCd}
                  disabled={adminYn === 'N'}
                >
                  <option disabled value="">
                    공장선택
                  </option>
                  <option value="ALL">전체</option>
                  {factoryOptions?.map(option => (
                    <option key={option.pltCd} value={option.pltCd}>
                      {option.pltNmKo}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              {/* 검사분류유형 Select Box*/}
              <Col sm={2} className="ps-1 pe-1">
                <Form.Select
                  required
                  size="sm"
                  onChange={({ target: { value } }) => setCtgryGrpCd(value)}
                  value={ctgryGrpCd}
                >
                  <option disabled value="">
                    검사분류유형
                  </option>
                  <option value="ALL">전체</option>
                  {categoryOptions?.map(option => (
                    <option key={option.ctgryCd} value={option.ctgryCd}>
                      {option.ctgryNmKr}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              {/* CCP관리 select-box */}
              <Col sm={1} className="ps-1 pe-1">
                <Form.Select
                  size="sm"
                  className="w-100"
                  onChange={({ target: { value } }) => setCcpYn(value)}
                  value={ccpYn}
                >
                  <option disabled value="">
                    CCP여부
                  </option>
                  <option value={'ALL'}>전체</option>
                  {CCP_YN_SELECT?.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.value}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              {/* 반송여부 select-box */}
              <Col sm={1} className="ps-1 pe-1">
                <Form.Select
                  size="sm"
                  className="w-100"
                  onChange={({ target: { value } }) => setSendBkYn(value)}
                  value={sendBkYn}
                >
                  <option disabled value="">
                    반송여부
                  </option>
                  <option value={'ALL'}>전체</option>
                  {RETURN_YN_SELECT.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.value}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              {/* 검사의뢰일 선택 */}
              <Col sm={2} className="ps-1 pe-1">
                <DatePicker
                  locale="ko"
                  onChange={onDateChange}
                  selected={regDtSt}
                  startDate={regDtSt}
                  endDate={regDtEn}
                  formatWeekDay={day => day.slice(0, 1)}
                  selectsRange
                  dateFormat="yyyy-MM-dd"
                  className="form-control form-control-sm"
                  placeholderText="검사의뢰일 선택"
                />
              </Col>
              {/* 구분 */}
              <Col sm={4} className="ps-1 pe-1">
                <InputGroup size="sm" style={{ width: '100%' }}>
                  <Form.Select
                    size="sm"
                    style={{ width: '35%' }}
                    value={divisionKey}
                    onChange={({ target: { value } }) => setDivisionKey(value)}
                  >
                    <option disabled value="">
                      구분선택
                    </option>
                    <option value={'sampleCd'}>품목코드</option>
                    <option value={'sampleNm'}>품목명</option>
                  </Form.Select>
                  <Form.Control
                    placeholder="구분 선택 후 입력해주세요."
                    type="text"
                    size="sm"
                    style={{ width: '65%' }}
                    maxLength="30"
                    value={divisionValue}
                    onChange={({ target: { value } }) =>
                      setDivisionValue(value)
                    }
                    disabled={divisionKey === ''}
                  />
                </InputGroup>
              </Col>
            </Row>
            <Row className="ms-0 me-0 mt-3">
              {/* 검사종류 선택 select-box */}
              <Col className="ps-1 pe-1">
                <Select
                  closeMenuOnSelect={false}
                  options={inspOptions}
                  placeholder="검사종류 선택"
                  isMulti
                  classNamePrefix="react-select"
                  value={inspTypeSeqList}
                  onChange={value => setInspTypeSeqList(value)}
                  styles={customStyles}
                />
              </Col>
              <Col
                sm={'auto'}
                className="ps-1 pe-1 d-flex flex-row align-items-center"
              >
                <Button size="sm" onClick={() => onSearch()}>
                  조회
                </Button>
                <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
                  검색 결과 : {comma(totalCount)} 건
                </p>
              </Col>
            </Row>
          </Form.Group>
          <Table bordered hover className="fs--1 text-center">
            <thead className="bg-200 text-700 align-middle border-300">
              <tr>
                <th className="text-nowrap">No.</th>
                <th className="text-nowrap">접수번호</th>
                <th className="text-nowrap">발송일자</th>
                <th className="text-nowrap">공장</th>
                <th className="text-nowrap">검사분류유형</th>
                <th className="text-nowrap">시료코드</th>
                <th className="text-nowrap">시료명</th>
                <th className="text-nowrap">채취월일</th>
                <th className="text-nowrap">모선명</th>
                <th className="text-nowrap">원산지명</th>
                <th className="text-nowrap">공급회사명</th>
                <th className="text-nowrap">검사결과의뢰일</th>
                <th className="text-nowrap">등록일시</th>
                <th className="text-nowrap">반송여부</th>
                <th className="text-nowrap">PDF</th>
              </tr>
            </thead>
            <tbody>
              {list?.map(
                (
                  {
                    seq,
                    no,
                    rcptNoVl,
                    sendYmd,
                    pltNm,
                    ctgryGrpNm,
                    sampleCd,
                    sampleNm,
                    coltYmd,
                    cshipNm,
                    coriNm,
                    splyCoNm,
                    inspReqDt,
                    regDttm,
                    sendBkYn,
                    orgnlFilePathValue = null
                  },
                  index
                ) => (
                  <tr key={index}>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {no}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {rcptNoVl}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {sendYmd || '-'}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {pltNm || '-'}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {ctgryGrpNm || '-'}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {sampleCd || '-'}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {sampleNm || '-'}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {coltYmd || '-'}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {cshipNm || '-'}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {coriNm || '-'}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {splyCoNm || '-'}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {inspReqDt || '-'}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {regDttm || '-'}
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => onDetail(seq)}
                    >
                      {sendBkYn || '-'}
                    </td>
                    <td>
                      {(orgnlFilePathValue !== null && (
                        <ActionButton
                          icon="file-pdf"
                          title="PDF"
                          variant="action"
                          className="p-0 lh-1"
                          onClick={() => {
                            onPdfOpen(orgnlFilePathValue);
                          }}
                        />
                      )) ||
                        '-'}
                    </td>
                  </tr>
                )
              )}
              {pageLoading === true && (
                <>
                  <tr>
                    <td colSpan={15} className="w-auto fw-bold">
                      데이터 조회중입니다....
                    </td>
                  </tr>
                </>
              )}
              {list.length < 1 && pageLoading === false && (
                <tr>
                  <td colSpan={15} className="w-auto">
                    위생검사결과 정보가 존재하지 않습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {totalCount > 0 && (
            <Paging
              key={totalCount}
              perPage={searchParams.size}
              length={10}
              currentPage={searchParams.page + 1}
              totalCount={totalCount}
              movePage={page =>
                setSearchParams({ ...searchParams, page: page - 1 })
              }
            ></Paging>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default List;
